import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Lock, CreditCard, LogOut, Settings, RefreshCw } from 'lucide-react';

/**
 * Hard, full-screen, non-dismissible paywall shown when the user's subscription
 * is 'suspended' (post 7-day grace). The user can only reach:
 *   - Update card page  → to restore access
 *   - Subscription page → to cancel
 *   - Settings page     → to manage profile
 *   - Log out
 * All other routes are blocked. Once payment succeeds, subscription_status
 * flips back to 'active' and this component unmounts automatically.
 */

// Routes where the paywall is intentionally NOT rendered so the user can
// actually complete the recovery flow.
const ALLOWED_PATHS = [
  '/subscription/update-payment',
  '/subscription',
  '/settings',
];

// Public/marketing pages — never block these
const PUBLIC_PATHS = [
  '/', '/login', '/register', '/forgot-password', '/reset-password',
  '/privacy', '/privacy-policy', '/terms', '/terms-of-service',
  '/refund-policy', '/biometric-policy', '/acceptable-use',
  '/about', '/faq', '/contact', '/blog',
];

function isAllowedPath(pathname) {
  if (ALLOWED_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'))) return true;
  if (PUBLIC_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'))) return true;
  if (pathname.startsWith('/admin')) return true;
  return false;
}

export default function SuspendedPaywall() {
  const { user, token, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [refreshing, setRefreshing] = useState(false);

  const isSuspended = user?.subscription_status === 'suspended';

  // While the paywall is on screen, poll every 20s so that if the user completes
  // payment in another tab / via webhook, we auto-detect and unmount.
  useEffect(() => {
    if (!isSuspended) return;
    const interval = setInterval(() => {
      if (refreshUser) refreshUser();
    }, 20000);
    return () => clearInterval(interval);
  }, [isSuspended, refreshUser]);

  // Nothing to render for non-suspended users OR on allowed recovery/public pages
  if (!isSuspended) return null;
  if (isAllowedPath(location.pathname)) return null;
  if (!token) return null; // safety: never show paywall to logged-out users

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      if (refreshUser) await refreshUser();
    } finally {
      setRefreshing(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-md p-4 overflow-y-auto"
      data-testid="suspended-paywall-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="paywall-title"
    >
      <div
        className="bg-white w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden my-8"
        data-testid="suspended-paywall-card"
      >
        {/* Red header strip */}
        <div className="bg-gradient-to-r from-red-600 to-rose-700 text-white px-6 py-5 flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
            <Lock className="w-6 h-6" />
          </div>
          <div>
            <h1 id="paywall-title" className="text-xl font-bold leading-tight">
              Your Account is Suspended
            </h1>
            <p className="text-white/90 text-sm">Payment on your subscription is overdue</p>
          </div>
        </div>

        <div className="px-6 py-6 space-y-5">
          <p className="text-gray-700 text-sm leading-relaxed">
            Your 7-day grace period has ended and payment is still outstanding.
            <br />
            <span className="text-gray-500">
              Try-on, portfolio uploads, chat and collaborations are paused. Your data is safe — nothing has been deleted.
            </span>
          </p>

          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
            <p className="text-sm text-emerald-800">
              <strong>Update your card to restore access instantly.</strong> The moment payment succeeds, everything comes back online — no waiting.
            </p>
          </div>

          <button
            onClick={() => navigate('/subscription/update-payment')}
            data-testid="paywall-update-card-btn"
            className="w-full bg-gradient-to-r from-rose-500 to-orange-500 hover:from-rose-600 hover:to-orange-600 text-white py-3.5 rounded-xl font-semibold flex items-center justify-center gap-2 shadow-md text-base"
          >
            <CreditCard className="w-5 h-5" />
            Update Card & Pay Now
          </button>

          <button
            onClick={() => navigate('/subscription')}
            data-testid="paywall-manage-subscription-btn"
            className="w-full bg-white border-2 border-gray-200 hover:border-rose-300 text-gray-700 py-3 rounded-xl font-medium flex items-center justify-center gap-2 text-sm"
          >
            <Settings className="w-4 h-4" />
            Manage or Cancel Subscription
          </button>

          <button
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="paywall-refresh-btn"
            className="w-full text-gray-500 hover:text-gray-700 py-2 rounded-xl text-xs flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Checking status…' : 'Already paid? Refresh status'}
          </button>

          <div className="pt-4 border-t border-gray-100 flex items-center justify-between text-xs">
            <button
              onClick={handleLogout}
              data-testid="paywall-logout-btn"
              className="text-gray-500 hover:text-gray-700 flex items-center gap-1.5"
            >
              <LogOut className="w-3.5 h-3.5" />
              Log out
            </button>
            <a
              href="mailto:help@virtualfit.work"
              className="text-gray-500 hover:text-rose-500"
              data-testid="paywall-support-link"
            >
              Need help? help@virtualfit.work
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
